/**
 * ContextPanel — Right panel of Ask Argus
 *
 * Demo-port design (R2): clean Sources panel showing collapsible per-doc
 * cards, with the production CitationModal as the drilled-in document
 * preview drawer.
 *
 * Citations are scoped to ONLY the most-recent assistant response's
 * citations (PR-AAU-C founder fix). The route hands us a `Citation[]`
 * extracted from the latest assistant `Message.citations` — these are
 * REAL citations emitted by the SSE `citation` event on /ecosystem/query,
 * NOT mock data.
 */

import { useState } from 'react';
import {
  FileText, ChevronRight, ExternalLink, Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { CitationModal } from '@/components/chat';
import type { Citation } from '@/lib/chat-types';
import { tierOf, tierStyle, presentTiers, CITATION_TIERS, type CitationTier } from '@/lib/citation-tier';

interface ContextPanelProps {
  citations: Citation[];
  /**
   * Whether an assistant turn is currently in flight. When true and
   * citations is empty, the panel shows a subtle "retrieving sources…"
   * hint instead of the empty state.
   */
  isStreaming?: boolean;
  /**
   * Whether the displayed citations belong to a finished assistant
   * response. Drives the empty-state copy: a finished response with
   * zero citations means "no sources for this answer", which is
   * different from "no assistant turn yet". Defaults to true.
   */
  hasResponse?: boolean;
}

export function ContextPanel({
  citations,
  isStreaming = false,
  hasResponse = true,
}: ContextPanelProps) {
  const [selectedDoc, setSelectedDoc] = useState<{ docName: string; citations: Citation[] } | null>(null);

  // Deduplicate citations by TIER + identity — never by docName alone. A web
  // result and a Library document that happen to share a title (e.g. a vendor
  // SOP whose web-page title equals the indexed doc title) must NOT collapse
  // into one card painted with whichever tier arrived first — that would render
  // an unverified web source as blue "Library · verified" (or vice-versa), a
  // truth-over-speed violation. Keying on tier guarantees each card is
  // tier-uniform.
  // Group by tier+identity, but TRACK each citation's 1-based position so each
  // source card can show the SAME number the inline [N] badge uses — the founder
  // asked for "a source number that maps to the panel". Numbers are the index in
  // the original citations array + 1, identical to MarkdownRenderer's badges.
  const uniqueDocs = new Map<string, { cits: Citation[]; numbers: number[] }>();
  citations.forEach((cit, idx) => {
    const key = `${tierOf(cit)}::${cit.documentId ?? cit.url ?? cit.docName}`;
    const existing = uniqueDocs.get(key) ?? { cits: [], numbers: [] };
    existing.cits.push(cit);
    existing.numbers.push(idx + 1);
    uniqueDocs.set(key, existing);
  });

  const showStreamingHint = isStreaming && uniqueDocs.size === 0;
  const showEmptyState = !isStreaming && uniqueDocs.size === 0;

  // Tiers present across all citations — drives the colour legend.
  const tiers = presentTiers(citations);
  // A doc group's tier is the tier of its citations (uniform per source).
  const tierForDoc = (cits: Citation[]): CitationTier => tierOf(cits[0] ?? {});

  return (
    <div className="flex flex-col h-full w-full overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border/30 shrink-0">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Sources</span>
          {uniqueDocs.size > 0 && (
            <Badge variant="secondary" className="h-4 min-w-[16px] text-[9px] px-1">
              {uniqueDocs.size}
            </Badge>
          )}
        </div>
        {/* Tier legend — only the tiers actually present in this answer. */}
        {tiers.length > 0 && (
          <div className="flex items-center gap-3 mt-2">
            {tiers.map((t) => {
              const s = CITATION_TIERS[t];
              return (
                <span key={t} className="inline-flex items-center gap-1" title={s.hint}>
                  <span className={cn('h-2 w-2 rounded-full', s.dot)} />
                  <span className="text-[10px] text-muted-foreground">
                    {s.label}
                    {!s.verified && <span className="text-muted-foreground/60"> · unverified</span>}
                  </span>
                </span>
              );
            })}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        <div className="p-4 space-y-3">
          {showStreamingHint && (
            <div
              className="flex items-center gap-2 text-xs text-muted-foreground py-2"
              data-testid="context-panel-streaming-hint"
            >
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              <span>Retrieving sources…</span>
            </div>
          )}

          {showEmptyState && (
            <div
              className="flex flex-col items-center justify-center py-12 text-center"
              data-testid="context-panel-empty"
            >
              <FileText className="h-8 w-8 text-muted-foreground/30 mb-3" />
              <p className="text-sm font-medium text-muted-foreground">
                {hasResponse ? 'No sources for this answer' : 'No sources yet'}
              </p>
              <p className="text-xs text-muted-foreground mt-1 max-w-[200px]">
                {hasResponse
                  ? 'Argus answered from its training rather than a cited document.'
                  : 'Sources will appear here when Argus cites documents in its responses.'}
              </p>
            </div>
          )}

          {Array.from(uniqueDocs.entries()).map(([key, group]) => {
            const docName = group.cits[0]?.docName ?? 'Source';
            return (
              <SourceCard
                key={key}
                docName={docName}
                citations={group.cits}
                numbers={group.numbers}
                tier={tierForDoc(group.cits)}
                onOpenDocument={() => setSelectedDoc({ docName, citations: group.cits })}
              />
            );
          })}
        </div>
      </div>

      {/* Document preview drawer — production CitationModal */}
      <CitationModal
        citations={selectedDoc?.citations.map((c) => ({
          docName: c.docName,
          page: c.page,
          section: c.section,
          text: c.text,
          score: c.score,
          classification: c.classification,
          documentId: c.documentId,
        })) ?? []}
        open={!!selectedDoc}
        onOpenChange={(open) => {
          if (!open) setSelectedDoc(null);
        }}
      />
    </div>
  );
}

// ── Source Card ──────────────────────────────────────────────────────────────

function SourceCard({
  docName,
  citations,
  numbers,
  tier,
  onOpenDocument,
}: {
  docName: string;
  citations: Citation[];
  numbers: number[];
  tier: CitationTier;
  onOpenDocument: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const s = tierStyle(tier);
  // The reference number(s) for this source — identical to the inline [N] badge.
  const tierBadge = CITATION_TIERS[tier].inlineBadge;
  const subSource = citations.find((c) => c.subSource)?.subSource ?? null;
  // Guard against missing/NaN scores from live-streamed citation events.
  const scoredCits = citations.filter((c) => Number.isFinite(c.score));
  const avgScore =
    scoredCits.length > 0
      ? scoredCits.reduce((sum, c) => sum + (c.score as number), 0) / scoredCits.length
      : null;

  return (
    <Card className={cn('overflow-hidden', s.accent)} data-testid="context-panel-source-card">
      <button
        className="w-full text-left px-3 py-2.5 hover:bg-muted/30 transition-colors overflow-hidden"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start gap-2 overflow-hidden">
          {/* Tier-colored reference number(s) — the SAME number as the inline [N]
              badge in the answer, so the reader can map prose → panel. */}
          <span className="flex items-center gap-0.5 mt-0.5 shrink-0">
            {numbers.slice(0, 3).map((n) => (
              <span
                key={n}
                className={cn(
                  'inline-flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold',
                  tierBadge,
                )}
              >
                {n}
              </span>
            ))}
          </span>
          <div className="flex-1 min-w-0 overflow-hidden">
            <p className="text-sm font-medium truncate">{docName}</p>
            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
              <Badge variant="outline" className={cn('text-[9px] h-4 px-1', s.badge)} title={s.hint}>
                {s.label}
                {subSource ? ` · ${subSource}` : ''}
              </Badge>
              <span className="text-[10px] text-muted-foreground">
                {citations.length} citation{citations.length > 1 ? 's' : ''}
              </span>
              {avgScore !== null && (
                <Badge variant="outline" className="text-[9px] h-4 px-1">
                  {(avgScore * 100).toFixed(0)}% avg
                </Badge>
              )}
            </div>
          </div>
          <ChevronRight
            className={cn(
              'h-4 w-4 text-muted-foreground transition-transform',
              expanded && 'rotate-90',
            )}
          />
        </div>
      </button>

      {expanded && (
        <div className="px-3 pb-3 space-y-2 border-t border-border/30 overflow-hidden">
          {citations.map((cit, i) => (
            <div key={i} className="mt-2 overflow-hidden">
              <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                {cit.page != null && <span className="shrink-0">Page {cit.page}</span>}
                {cit.section && <span className="truncate">— {cit.section}</span>}
                {Number.isFinite(cit.score) && (
                  <Badge variant="outline" className="text-[9px] h-3.5 px-1 ml-auto shrink-0">
                    {((cit.score as number) * 100).toFixed(0)}%
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1 line-clamp-3 break-words">
                {cit.text}
              </p>
            </div>
          ))}
          {tier === 'web' ? (
            (() => {
              const url = citations.find((c) => c.url)?.url ?? null;
              return url ? (
                <Button asChild variant="ghost" size="sm" className="w-full h-7 text-xs mt-2">
                  <a href={url} target="_blank" rel="noopener noreferrer">
                    <ExternalLink className="h-3 w-3 mr-1" />
                    Open Link
                  </a>
                </Button>
              ) : null;
            })()
          ) : tier === 'ai' ? null : (
            <Button
              variant="ghost"
              size="sm"
              className="w-full h-7 text-xs mt-2"
              onClick={onOpenDocument}
            >
              <ExternalLink className="h-3 w-3 mr-1" />
              Open Document
            </Button>
          )}
        </div>
      )}
    </Card>
  );
}
